import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import merchantsApi, { useCreateMerchantsBankAccountMutation } from 'services/merchants';
import { BridgeFiatRail, ExternalAccountType, FiatPayoutSettingsPayload, MerchantPayoutFlow } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { FormField } from 'components/form/formField/formField';
import { Input } from 'components/inputs';
import { Buttons } from 'components/modal/styles';

import { ReduxToolkitError } from 'types/http';

import Logger from 'utils/logger';

import { usePayout } from '../../../payoutModal/context';
import { usePayoutSetupStep } from '../../../payoutModal/context/modal';
import { ErrorText } from '../../../payoutModal/fiat/styles';
import { Delimiter } from '../../../payoutModal/styles';
import { PayoutType } from '../../../payoutModal/types';
import { LABELS } from '../../keys';
import { Label, Section } from '../form.styles';
import { BrebFormValues, getBrebInitialValues, getBrebValidationSchema } from './breb.keys';

const BrebBankAccountForm = () => {
  const { t } = useTranslation();
  const payout = usePayout();
  const payoutStep = usePayoutSetupStep();
  const previousValues = payout[PayoutType.Fiat][BridgeFiatRail.BRE_B]?.form?.values;
  const [createBankAccount, { isLoading, error }] = useCreateMerchantsBankAccountMutation();
  const [updatePayoutSettings, { isLoading: isUpdatingPayoutSettings }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const form = useFormik<BrebFormValues>({
    initialValues: previousValues ?? getBrebInitialValues(),
    validationSchema: getBrebValidationSchema(),
    onSubmit: async (values) => {
      const response = await createBankAccount({
        uuid: payout.merchant.uuid,
        paymentRail: BridgeFiatRail.BRE_B,
        accountType: ExternalAccountType.BRE_B,
        currency: 'cop',
        bankName: values.bankName,
        accountOwnerName: values.accountOwnerName,
        breB: { breBKey: values.breBKey.trim() },
      });

      if ('error' in response || !response.data?.data?.uuid) {
        return;
      }

      const shouldUpdatePaymentRail = payout.merchant.payoutFlow !== MerchantPayoutFlow.DIRECT;
      const payload: FiatPayoutSettingsPayload = {
        merchantId: payout.merchant.uuid,
        payoutDestinationUuid: response.data.data.uuid,
        payoutMemo: '',
        ...(shouldUpdatePaymentRail && {
          paymentRail: BridgeFiatRail.BRE_B,
        }),
      };
      const payoutSettingsResponse = await updatePayoutSettings(payload);
      if ('error' in payoutSettingsResponse) {
        Logger.error('Unable to update main account after Bre-B destination was created:', JSON.stringify(payoutSettingsResponse));
        return;
      }

      payoutStep.proceed();
    },
  });

  useEffect(() => {
    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        [BridgeFiatRail.BRE_B]: { form },
      },
    });
  }, [form.values]);

  const fieldError = (field: keyof BrebFormValues) => (form.touched[field] ? (form.errors[field] as string | undefined) : undefined);

  return (
    <>
      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BREB_KEY_SECTION.TITLE)}</Label>
        <FormField error={fieldError('breBKey')}>
          <Input
            value={form.values.breBKey}
            onChange={(value) => form.setFieldValue('breBKey', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BREB_KEY)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_NAME_SECTION.TITLE)}</Label>
        <FormField error={fieldError('accountOwnerName')}>
          <Input
            value={form.values.accountOwnerName}
            onChange={(value) => form.setFieldValue('accountOwnerName', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_OWNER_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BANK_NAME_SECTION.TITLE)}</Label>
        <FormField error={fieldError('bankName')}>
          <Input
            value={form.values.bankName}
            onChange={(value) => form.setFieldValue('bankName', value)}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BANK_NAME)}
          />
        </FormField>
      </Section>

      {Boolean((error as ReduxToolkitError)?.data?.errorMessage) && (
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
      )}

      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.goBack}>
          {t(LABELS.ACTIONS.BACK)}
        </Button>
        <ButtonWithLoading
          primary
          isLoading={isLoading || isUpdatingPayoutSettings}
          disabled={!form.isValid}
          onClick={() => form.handleSubmit()}
        >
          {t(LABELS.ACTIONS.ADD)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default BrebBankAccountForm;
